/* eslint-disable react/prop-types */
import React, { useState, useEffect } from "react";
import { AppDispatch } from "web/redux/store";
import analytics from "web/script/analytics/analytics";
import CloseButton from "web/react/components/buttons/close-button/close-button";
import SVGIcon from "web/react/components/svg-icon/svg-icon";
import { gettext } from "web/script/modules/django-i18n";
import classnames from "classnames";
import { hideMainNavigation } from "web/redux/ducks/main-navigation";
import styles from "./main-navigation-desktop.css";

const CATEGORY_LIMIT = 12;

const letters = "ABCDEFGHIJKLMNOPQRSTUVWXYZ".split("");
letters.push("0-9");

interface Menu {
    name: string;
    menus: Menu[];
    trend_menus: any[];
    display_name: string;
    links: Link[];
}

interface Link {
    text: string;
    is_sale: boolean;
    url: string;
}

interface MainNavigationDesktopProps {
    menuData: {
        menus: Menu[];
        links: Link[];
    };
    featuredDesignersUrl: string;
    visible: boolean;
    dispatch: AppDispatch;
}

export function MainNavigationDesktop({
    menuData,
    featuredDesignersUrl,
    visible,
    dispatch,
}: MainNavigationDesktopProps): React.ReactElement {
    const categories = menuData.menus;
    const links = menuData.links;
    const iconName = "arrow-icon";
    const [selectedCategory, setSelectedCategory] = useState(menuData.menus[0]);
    const [selectedSubMenu, setSelectedSubMenu] = useState(menuData.menus[0].menus[0]);
    const [selectedTrendMenu, setSelectedTrendMenu] = useState<any>(null);
    const [popularBrands, setPopularBrands] = useState<Link[] | null>(null);
    const [expandedCategories, setExpandedCategories] = useState({});

    function selectCategory(category): void {
        setSelectedCategory(category);
        setSelectedSubMenu(category.menus[0]);
        setSelectedTrendMenu(null);
        setPopularBrands(null);
    }

    function selectPopularBrands(category): void {
        setSelectedCategory(category);
        setSelectedSubMenu(menuData.menus[0].menus[0]);
        setSelectedTrendMenu(null);
        setPopularBrands(category.links);
    }

    useEffect(() => {
        setSelectedCategory(menuData.menus[0]);
        setSelectedSubMenu(menuData.menus[0].menus[0]);
        setSelectedTrendMenu(null);
        setPopularBrands(null);
    }, [menuData]);

    function getInteraction(callback, label): { onClick: () => void } {
        return {
            ...{
                onClick: (): void => {
                    analytics.event("Menu click", "Clicked", label);
                    callback();
                },
            },
        };
    }

    function renderCategories(): React.ReactElement {
        return (
            <ul className={styles.menu}>
                {categories.map((category) => {
                    const isPopularBrands = category.name === "popular_brands";
                    function interact(): void {
                        if (isPopularBrands) {
                            selectPopularBrands(category);
                        } else {
                            selectCategory(category);
                        }
                    }
                    const interaction = getInteraction(interact, "Category");
                    let displayText;

                    if (isPopularBrands) {
                        displayText = gettext("app_header_menu.brands_title");
                    }

                    if (!displayText) {
                        displayText = category.display_name;
                    }
                    return (
                        <li
                            key={category.display_name}
                            className={classnames(styles.menuItem, {
                                [styles.activeMenuItem]: selectedCategory.name === category.name,
                                [styles.clickable]: true,
                            })}
                            {...interaction}
                        >
                            {displayText}
                        </li>
                    );
                })}
                {links &&
                    links.map((link) => {
                        const isSaleLink = link.is_sale;
                        return (
                            <li key={link.text} className={classnames(styles.menuItem)}>
                                {isSaleLink && (
                                    <SVGIcon className={classnames(styles.saleTag)} name="tag" />
                                )}
                                <a href={link.url} className={styles.saleLink}>
                                    {link.text}
                                </a>
                            </li>
                        );
                    })}
            </ul>
        );
    }

    function renderSubCategories(): React.ReactElement {
        const totalItems = selectedCategory.menus.length + selectedCategory.links.length;
        const hasHiddenItems = totalItems >= CATEGORY_LIMIT;
        const hideItems = hasHiddenItems && !expandedCategories[selectedCategory.name];

        return (
            <div
                className={classnames(styles.subMenu, {
                    [styles.hideExtraLinks]: hideItems,
                })}
            >
                <ul className={styles.subMenu}>
                    {selectedCategory.menus.map((menu) => {
                        function interact(): void {
                            setSelectedSubMenu(menu);
                            setSelectedTrendMenu(null);
                        }
                        const interaction = getInteraction(interact, "Subcategory");
                        return (
                            <li
                                key={menu.display_name}
                                className={classnames(styles.subMenuItem, {
                                    [styles.subMenuItemActive]: selectedSubMenu.name === menu.name,
                                    [styles.clickable]: true,
                                })}
                                {...interaction}
                            >
                                {menu.display_name}
                                <SVGIcon className={styles.subMenuChevron} name={iconName} />
                            </li>
                        );
                    })}

                    {selectedCategory.links &&
                        selectedCategory.links.map((link) => {
                            return (
                                <li
                                    key={link.text}
                                    className={classnames(styles.subMenuItem, {
                                        [styles.saleMenuItem]: link.is_sale,
                                    })}
                                >
                                    {link.is_sale && (
                                        <SVGIcon className={styles.saleTag} name="tag" />
                                    )}
                                    <a href={link.url} className={styles.subMenuLink}>
                                        {link.text}
                                    </a>
                                </li>
                            );
                        })}
                </ul>
                {hideItems && (
                    <button
                        type="button"
                        className={styles.moreButton}
                        onClick={(): void =>
                            setExpandedCategories({
                                ...expandedCategories,
                                [selectedCategory.name]: true,
                            })
                        }
                    >
                        {"+ "}
                        {gettext("burger_menu.more_category_link")} {/* More */}
                    </button>
                )}
                <ul className={styles.trendList}>
                    {selectedCategory.trend_menus &&
                        selectedCategory.trend_menus.map((trendMenu) => {
                            function interact(): void {
                                setSelectedSubMenu(trendMenu);
                                setSelectedTrendMenu(null);
                            }
                            const interaction = getInteraction(interact, "Trends");
                            return (
                                <li
                                    key={trendMenu.display_name}
                                    className={classnames(styles.subMenuItem, styles.trendItem, {
                                        [styles.subMenuItemActive]:
                                            selectedSubMenu.name === trendMenu.name,
                                        [styles.clickable]: true,
                                    })}
                                    {...interaction}
                                >
                                    {trendMenu.display_name}
                                    <SVGIcon className={styles.subMenuChevron} name={iconName} />
                                </li>
                            );
                        })}
                </ul>
            </div>
        );
    }

    function renderSubCategoryLinks(): React.ReactElement {
        return (
            <ul className={styles.subMenu}>
                {Array.isArray(selectedSubMenu.links) &&
                    selectedSubMenu.links.map((link) => {
                        if (link.url === "") return;
                        return (
                            <li
                                key={link.text}
                                className={classnames(styles.subMenuItem, {
                                    [styles.saleMenuItem]: link.is_sale,
                                })}
                            >
                                {link.is_sale && <SVGIcon className={styles.saleTag} name="tag" />}
                                <a href={link.url} className={styles.subMenuLink}>
                                    {link.text}
                                </a>
                            </li>
                        );
                    })}

                {Array.isArray(selectedSubMenu.trend_menus) &&
                    selectedSubMenu.trend_menus.map((trendMenu) => {
                        const interaction = getInteraction(
                            () => setSelectedTrendMenu(trendMenu),
                            "Trends"
                        );
                        return (
                            <li
                                key={trendMenu.display_name}
                                className={classnames(styles.subMenuItem, styles.trendItem, {
                                    [styles.subMenuItemActive]:
                                        selectedTrendMenu &&
                                        selectedTrendMenu.name === trendMenu.name,
                                    [styles.clickable]: true,
                                })}
                                {...interaction}
                            >
                                {trendMenu.display_name}
                                <SVGIcon className={styles.subMenuChevron} name={iconName} />
                            </li>
                        );
                    })}
            </ul>
        );
    }

    function renderTrendLinks(): React.ReactElement | null {
        if (!selectedTrendMenu) {
            return null;
        }

        return (
            <ul className={styles.subMenu}>
                {selectedTrendMenu.links.map((link) => {
                    return (
                        <li
                            key={link.url}
                            className={classnames(styles.subMenuItem, {
                                [styles.saleMenuItem]: link.is_sale,
                            })}
                        >
                            <a href={link.url} className={styles.subMenuLink}>
                                {link.text}
                            </a>
                        </li>
                    );
                })}
            </ul>
        );
    }

    function renderPopularBrands(): React.ReactElement {
        return (
            <div className={styles.subMenusContainer}>
                <div className={styles.popularBrands}>
                    <span className={styles.subMenuTitle}>
                        {gettext("app_header_menu.popular_brands")}
                    </span>
                    <ul className={styles.popularBrandsList}>
                        {popularBrands &&
                            popularBrands.map((link) => (
                                <li
                                    key={link.url}
                                    className={classnames(
                                        styles.subMenuItem,
                                        styles.popularBrandItem
                                    )}
                                >
                                    <a href={link.url} className={classnames(styles.subMenuLink)}>
                                        {link.text}
                                    </a>
                                </li>
                            ))}
                    </ul>
                </div>
                <div>
                    <span className={styles.subMenuTitle}>
                        {gettext("app_header_menu.brands_a_z")}
                    </span>
                    <div className={styles.brandLetters}>
                        {letters.map((letter) => (
                            <a
                                key={`brand-initial-${letter}`}
                                href={`${featuredDesignersUrl}#${letter}`}
                                className={styles.brandLetter}
                            >
                                {letter}
                            </a>
                        ))}
                    </div>
                    <a href={featuredDesignersUrl} className={styles.viewAllBrands}>
                        {gettext("app_header_menu.view_all")}
                    </a>
                </div>
            </div>
        );
    }

    return (
        <>
            <div
                className={classnames(styles.mainNavigationOverlay, {
                    [styles.show]: visible,
                })}
                onClick={(): void => {
                    dispatch(hideMainNavigation());
                }}
            />
            <div
                className={classnames(styles.mainNavigationDesktop, {
                    [styles.show]: visible,
                })}
            >
                {renderCategories()}
                <CloseButton
                    className={styles.closeButton}
                    onClick={(): void => {
                        dispatch(hideMainNavigation());
                    }}
                />
                {popularBrands ? (
                    renderPopularBrands()
                ) : (
                    <div className={styles.subMenusContainer}>
                        {renderSubCategories()}
                        {renderSubCategoryLinks()}
                        {renderTrendLinks()}
                    </div>
                )}
            </div>
        </>
    );
}

export default MainNavigationDesktop;
